import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import type { ChecklistItem, ChecklistTemplateItem } from "@/lib/db-types";
import { SrIcon } from "@/components/ui/SrIcon";
import { snapshotChecklist } from "@/hooks/use-project-meetings";

/** Sjekkliste-utseende (grønn hake) oppå den delte Checkbox-primitiven. */
const CHECKLIST_CHECKBOX_CLASS =
  "mt-0.5 h-5 w-5 rounded border-2 border-gray-300 group-hover:border-emerald-400 data-[state=checked]:border-emerald-500 data-[state=checked]:bg-emerald-500 data-[state=checked]:text-white";

export interface MeetingDialogState {
  /** "Gjennomført" | "Ikke nødvendig" | null. Default ved nye rader er "Gjennomført". */
  status: string | null;
  reason: string | null;
  checklist: ChecklistItem[];
  notes: string | null;
  /** ISO-dato (YYYY-MM-DD). Defaultes til i dag ved opprettelse. */
  heldAt: string | null;
  /** VIB-788: kun brukt for debrief. */
  whatWentWell?: string | null;
  whatWentPoorly?: string | null;
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

/**
 * Bygger initial dialog-state ut fra eksisterende DB-rad eller defaults
 * for en ny rad. Kalles av parent ved åpning.
 */
export function buildInitialDialogState({
  existing,
  defaultItems,
}: {
  existing: {
    status?: string | null;
    reason?: string | null;
    checklist?: ChecklistItem[];
    notes?: string | null;
    held_at?: string | null;
    what_went_well?: string | null;
    what_went_poorly?: string | null;
  } | null;
  defaultItems: ChecklistTemplateItem[];
}): MeetingDialogState {
  if (!existing) {
    return {
      status: "Gjennomført",
      reason: null,
      checklist: snapshotChecklist(defaultItems),
      notes: null,
      heldAt: todayIso(),
      whatWentWell: null,
      whatWentPoorly: null,
    };
  }
  return {
    status: existing.status ?? "Gjennomført",
    reason: existing.reason ?? null,
    checklist: existing.checklist && existing.checklist.length > 0
      ? existing.checklist
      : snapshotChecklist(defaultItems),
    notes: existing.notes ?? null,
    heldAt: existing.held_at ? existing.held_at.slice(0, 10) : todayIso(),
    whatWentWell: existing.what_went_well ?? null,
    whatWentPoorly: existing.what_went_poorly ?? null,
  };
}

export function MeetingChecklistDialog({
  open,
  onOpenChange,
  label,
  initialState,
  defaultItems,
  showLearningFields = false,
  onSave,
  onReset,
  saving = false,
  resetting = false,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  label: string;
  initialState: MeetingDialogState;
  defaultItems: ChecklistTemplateItem[];
  showLearningFields?: boolean;
  onSave: (state: MeetingDialogState) => Promise<void> | void;
  /** Slett alle data for denne møterad — kalles ved Nullstill-knapp. */
  onReset?: () => Promise<void> | void;
  saving?: boolean;
  resetting?: boolean;
}) {
  const [state, setState] = useState<MeetingDialogState>(initialState);

  // Resett til initialState hver gang dialogen åpnes
  useEffect(() => {
    if (open) {
      setState(initialState);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const items: ChecklistItem[] =
    state.checklist.length > 0 ? state.checklist : snapshotChecklist(defaultItems);
  const checkedCount = items.filter((i) => i.checked).length;
  const allChecked = items.length > 0 && checkedCount === items.length;
  const isNotNeeded = state.status === "Ikke nødvendig";

  const handleToggleItem = (key: string) => {
    const updated = items.map((i) => (i.key === key ? { ...i, checked: !i.checked } : i));
    setState({ ...state, checklist: updated });
  };

  const handleToggleIkkeNodvendig = () => {
    if (isNotNeeded) {
      // Tilbake til skjema-modus
      setState({ ...state, status: "Gjennomført", reason: null });
    } else {
      setState({ ...state, status: "Ikke nødvendig" });
    }
  };

  const handleSave = async () => {
    // Lagring av møteskjemaet betyr at møtet er holdt. Normaliser status til
    // en kanonisk verdi så chipen blir grønn ("Gjennomført"), uavhengig av om
    // raden bærer en eldre/ukjent legacy-status (f.eks. importert "Ikke utført").
    // "Ikke nødvendig"-grenen beholder sin egen status.
    const normalizedStatus = isNotNeeded ? "Ikke nødvendig" : "Gjennomført";
    await onSave({ ...state, status: normalizedStatus });
  };

  const handleReset = async () => {
    if (!onReset) return;
    const ok = window.confirm("Nullstille skjema og status? Lagrede data slettes.");
    if (!ok) return;
    await onReset();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{label}</DialogTitle>
        </DialogHeader>

        {/* Dato — alltid synlig */}
        <div className="space-y-1">
          <label className="text-xs font-medium text-muted-foreground">Dato</label>
          <Input
            type="date"
            value={state.heldAt ?? todayIso()}
            onChange={(e) => setState({ ...state, heldAt: e.target.value || todayIso() })}
            className="text-sm"
          />
        </div>

        {isNotNeeded ? (
          <div className="space-y-2 mt-4">
            <p className="text-sm text-muted-foreground">Begrunn hvorfor møtet ikke er nødvendig:</p>
            <Textarea
              placeholder="Årsak..."
              value={state.reason ?? ""}
              onChange={(e) => setState({ ...state, reason: e.target.value })}
              className="text-sm min-h-[100px] resize-y"
            />
          </div>
        ) : (
          <>
            {/* Sjekkliste */}
            <div className="space-y-3 mt-4">
              <p className="text-sm text-muted-foreground">
                Sjekkliste ({checkedCount}/{items.length}):
              </p>
              {items.map((item) => (
                <label key={item.key} className="flex items-start gap-3 cursor-pointer group">
                  {/* Ekte checkbox så Tab/Space virker (VIB-2529). Label-wrapperen gir klikk på teksten. */}
                  <Checkbox
                    checked={item.checked}
                    onCheckedChange={() => handleToggleItem(item.key)}
                    aria-label={item.label}
                    className={CHECKLIST_CHECKBOX_CLASS}
                  />
                  <span className={cn("text-sm", item.checked && "line-through text-muted-foreground")}>
                    {item.label}
                  </span>
                </label>
              ))}
              {allChecked && (
                <div className="flex items-center gap-2 text-emerald-600 bg-emerald-50 rounded-lg px-3 py-2 mt-2">
                  <SrIcon name="check-circle2" className="w-4 h-4" />
                  <span className="text-sm font-medium">Alle punkter fullført</span>
                </div>
              )}
            </div>

            {/* VIB-788: Læringsfelt (kun debrief) */}
            {showLearningFields && (
              <div className="space-y-3 mt-4">
                <div className="space-y-2">
                  <p className="text-sm font-medium text-emerald-700">Hva gikk bra</p>
                  <Textarea
                    placeholder="Hva fungerte godt? Skriv ett punkt per linje..."
                    value={state.whatWentWell ?? ""}
                    onChange={(e) => setState({ ...state, whatWentWell: e.target.value })}
                    className="text-sm min-h-[80px] resize-y"
                  />
                </div>
                <div className="space-y-2">
                  <p className="text-sm font-medium text-amber-700">Hva gikk dårlig</p>
                  <Textarea
                    placeholder="Hva kan forbedres? Skriv ett punkt per linje..."
                    value={state.whatWentPoorly ?? ""}
                    onChange={(e) => setState({ ...state, whatWentPoorly: e.target.value })}
                    className="text-sm min-h-[80px] resize-y"
                  />
                </div>
              </div>
            )}

            {/* Notater */}
            <div className="space-y-2 mt-4">
              <p className="text-sm font-medium">Notater</p>
              <Textarea
                placeholder="Andre notater fra møtet..."
                value={state.notes ?? ""}
                onChange={(e) => setState({ ...state, notes: e.target.value })}
                className="text-sm min-h-[80px] resize-y"
              />
            </div>
          </>
        )}

        {/* Actions */}
        <div className="flex justify-between items-center mt-6 gap-2 flex-wrap">
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleToggleIkkeNodvendig}
              className={cn(
                isNotNeeded ? "text-emerald-600" : "text-muted-foreground hover:text-foreground"
              )}
            >
              <SrIcon name={isNotNeeded ? "arrow-left" : "x-circle"} className="w-4 h-4 mr-1" />
              {isNotNeeded ? "Tilbake til skjema" : "Ikke nødvendig"}
            </Button>
            {onReset && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleReset}
                disabled={resetting || saving}
                className="text-muted-foreground hover:text-destructive"
              >
                <SrIcon name="rotate-ccw" className="w-4 h-4 mr-1" />
                {resetting ? "Nullstiller..." : "Nullstill"}
              </Button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={saving}>
              Avbryt
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? "Lagrer..." : "Lagre"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default MeetingChecklistDialog;
